import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { workflowApi } from '../services/api';
import { EmptyState, LoadingState } from '../components/ui';
import { fehlerText } from '../vertrieb/format';

// Bekannte Vorlagen aus dem Serien-Workflow -> Namensmuster des Workflows.
const TEMPLATE_NAME_MAP: Record<string, string> = {
  'prompt-5': 'Prompt 5',
  'prompt-6': 'Prompt 6',
  'prompt-7': 'Prompt 7',
  'prompt-8': 'Prompt 8',
};

/**
 * Sprungziel aus dem Serien-Workflow: sucht den passenden gespeicherten
 * Workflow und leitet dorthin weiter. Fehlt er, lassen sich die Vorlagen
 * direkt hier laden.
 */
const ExecuteTemplatePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { push } = useToast();
  const template = params.get('template');
  const seriesFormatId = params.get('seriesFormatId');
  const [fehler, setFehler] = useState<string | null>(null);
  const [fehltVorlage, setFehltVorlage] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [laedtVorlagen, setLaedtVorlagen] = useState(false);

  const aufloesen = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    setFehltVorlage(false);
    const muster = template ? TEMPLATE_NAME_MAP[template] : undefined;
    if (!template || !muster) {
      setFehler(template ? `Unbekannte Vorlage „${template}“.` : 'Keine Vorlage angegeben.');
      setLaedt(false);
      return;
    }
    try {
      const alle = (await workflowApi.getWorkflows()) ?? [];
      const treffer = alle.find((w: any) => String(w.name).includes(muster));
      if (treffer) {
        navigate(`/workflows/${treffer.id}/execute`, { replace: true });
        return;
      }
      setFehltVorlage(true);
      setFehler(`Der Workflow „${muster}“ ist noch nicht angelegt.`);
    } catch (e) {
      setFehler(fehlerText(e, 'Workflows konnten nicht geladen werden'));
    }
    setLaedt(false);
  }, [template, navigate]);

  useEffect(() => { aufloesen(); }, [aufloesen]);

  const vorlagenLaden = async () => {
    setLaedtVorlagen(true);
    try {
      const b = await workflowApi.seedWorkflows();
      push({ message: b?.created ? `${b.created} Workflow-Vorlagen angelegt.` : 'Vorlagen sind vorhanden.', severity: 'success' });
      await aufloesen();
    } catch (e) {
      setFehler(fehlerText(e, 'Vorlagen konnten nicht geladen werden'));
    } finally {
      setLaedtVorlagen(false);
    }
  };

  if (laedt) return <div className="page-container"><LoadingState rows={2} label="Workflow wird gesucht" /></div>;

  return (
    <div className="page-container">
      <EmptyState
        icon="workflow"
        title={fehltVorlage ? 'Workflow-Vorlage fehlt' : 'Workflow nicht gefunden'}
        description={fehler ?? undefined}
        action={fehltVorlage
          ? { label: laedtVorlagen ? 'Lädt…' : 'Vorlagen laden', onClick: vorlagenLaden }
          : { label: 'Meine Workflows', to: '/workflows' }}
        secondaryAction={seriesFormatId
          ? { label: 'Zurück zum Serien-Workflow', to: `/series-formats/${seriesFormatId}/workflow` }
          : { label: 'Zurück', onClick: () => navigate(-1) }}
      />
      {fehltVorlage && <p className="text-xs text-secondary">Oder unter <Link to="/workflows">Meine Workflows</Link> einen eigenen Ablauf anlegen.</p>}
    </div>
  );
};

export default ExecuteTemplatePage;
